import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CognitoGroup, Doctor } from 'src/API';
import { StaffService } from 'src/app/core/api/staff.service';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { areQuestionnaireSelectionsCompatible } from 'src/app/core/api/questionnaire-selection.util';
import {
  MAX_ONE_CLICK_QRE_SET_NAME_LENGTH,
  MAX_ONE_CLICK_QRE_SETS_PER_CLINIC,
  MIN_ONE_CLICK_QRE_SET_ITEM_COUNT,
  ONE_CLICK_QRE_SETS_EXTRA_CONFIG_KEY,
  OneClickQRESet,
  OneClickQRESetItem,
  OneClickQRESetNameValidation,
  OneClickQRESetResolveResult
} from 'src/app/core/models/one-click-qre-set.model';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { v4 as uuid } from 'uuid';

export class OneClickQRESetError extends Error {
  constructor(public readonly errorKey: string) {
    super(errorKey);
    this.name = 'OneClickQRESetError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class OneClickQRESetService {
  constructor(
    private clinicSetupService: ClinicSetupService,
    private staffService: StaffService
  ) {}

  public getSets(): OneClickQRESet[] {
    const clinicSetup = this.clinicSetupService.clinicSetup;
    if (clinicSetup && clinicSetup.extraConfig) {
      return this.parseSetsFromExtraConfig(clinicSetup.extraConfig);
    }
    const loadedExtraConfig = this.clinicSetupService.clinicSetupFormGroup.getLoadedExtraConfig();
    return this.normalizeSets(loadedExtraConfig[ONE_CLICK_QRE_SETS_EXTRA_CONFIG_KEY]);
  }

  public parseSetsFromExtraConfig(extraConfigJson: string): OneClickQRESet[] {
    const extraConfig = safeJsonParse(extraConfigJson);
    if (!extraConfig || typeof extraConfig !== 'object') {
      return [];
    }
    return this.normalizeSets(extraConfig[ONE_CLICK_QRE_SETS_EXTRA_CONFIG_KEY]);
  }

  public validateName(
    name: string,
    excludeSetId?: string,
    allowExistingName?: boolean
  ): OneClickQRESetNameValidation {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      return {
        valid: false,
        trimmedName,
        errorKey: 'Template name is required'
      };
    }
    if (trimmedName.length > MAX_ONE_CLICK_QRE_SET_NAME_LENGTH) {
      return {
        valid: false,
        trimmedName,
        errorKey: 'Template name must be 30 characters or fewer'
      };
    }

    if (!allowExistingName) {
      const duplicate = this.getSets().some(set => {
        if (excludeSetId && set.id === excludeSetId) {
          return false;
        }
        return set.name.toLowerCase() === trimmedName.toLowerCase();
      });
      if (duplicate) {
        return {
          valid: false,
          trimmedName,
          errorKey: 'A template with this name already exists'
        };
      }
    }

    return { valid: true, trimmedName, errorKey: '' };
  }

  public validateItems(items: OneClickQRESetItem[], intakeFormsEnabled: boolean): string {
    if (!items || items.length < MIN_ONE_CLICK_QRE_SET_ITEM_COUNT) {
      return 'Select at least two questionnaires';
    }

    const seenItemKeys: { [key: string]: boolean } = {};
    const hasDuplicate = items.some(item => {
      const itemKey = item.kind + ':' + item.id;
      if (seenItemKeys[itemKey]) {
        return true;
      }
      seenItemKeys[itemKey] = true;
      return false;
    });
    if (hasDuplicate) {
      return 'Questionnaires in a template must be unique';
    }

    const selectedIds = items.map(item => item.id);
    if (!areQuestionnaireSelectionsCompatible(selectedIds, intakeFormsEnabled)) {
      return 'These questionnaires cannot be saved together';
    }

    return '';
  }

  public canMutateAllSets(): boolean {
    const staff = this.staffService.staff;
    if (!staff) {
      return false;
    }
    if (
      this.staffService.isClinicOwner ||
      this.staffService.isClinicAdmin ||
      this.staffService.isDoctor
    ) {
      return true;
    }
    return this.staffHasPrivilegedGroup(staff);
  }

  public canMutateSet(set: OneClickQRESet): boolean {
    return !!set && this.canMutateAllSets();
  }

  public canCreateSet(): boolean {
    if (!this.canMutateAllSets()) {
      return false;
    }
    return this.getSets().length < MAX_ONE_CLICK_QRE_SETS_PER_CLINIC;
  }

  public canSaveSet(selectedCount: number): boolean {
    if (!this.canMutateAllSets()) {
      return false;
    }
    if (!selectedCount || selectedCount < MIN_ONE_CLICK_QRE_SET_ITEM_COUNT) {
      return false;
    }
    if (this.canCreateSet()) {
      return true;
    }
    return this.getSets().some(set => this.canMutateSet(set));
  }

  public resolveSetItems(
    set: OneClickQRESet,
    availableIds: { [id: string]: boolean }
  ): OneClickQRESetResolveResult {
    const availableItems: OneClickQRESetItem[] = [];
    const missingItems: OneClickQRESetItem[] = [];
    if (!set || !set.items) {
      return { availableItems, missingItems };
    }
    set.items.forEach(item => {
      if (item && item.id && availableIds[item.id]) {
        availableItems.push(item);
      } else if (item) {
        missingItems.push(item);
      }
    });
    return { availableItems, missingItems };
  }

  public createSet(
    name: string,
    items: OneClickQRESetItem[],
    intakeFormsEnabled: boolean
  ): Observable<OneClickQRESet[]> {
    const nameValidation = this.validateName(name);
    if (!nameValidation.valid) {
      return throwError(new OneClickQRESetError(nameValidation.errorKey));
    }
    const itemsError = this.validateItems(items, intakeFormsEnabled);
    if (itemsError) {
      return throwError(new OneClickQRESetError(itemsError));
    }
    if (!this.canMutateAllSets()) {
      return throwError(new OneClickQRESetError('You cannot create a template'));
    }
    if (this.getSets().length >= MAX_ONE_CLICK_QRE_SETS_PER_CLINIC) {
      return throwError(new OneClickQRESetError('Maximum of 5 templates allowed'));
    }

    const staff = this.staffService.staff;
    const createdByStaffId = staff && staff.id ? staff.id : '';
    const newSet: OneClickQRESet = {
      id: uuid(),
      name: nameValidation.trimmedName,
      createdByStaffId,
      items: items.map(item => ({ ...item }))
    };

    return this.persistSets([...this.getSets(), newSet]);
  }

  public updateSet(
    setId: string,
    changes: { name?: string; items?: OneClickQRESetItem[] },
    intakeFormsEnabled: boolean
  ): Observable<OneClickQRESet[]> {
    const sets = this.getSets();
    const existingSet = sets.find(set => set.id === setId);
    if (!existingSet) {
      return throwError(new OneClickQRESetError('Template not found'));
    }
    if (!this.canMutateSet(existingSet)) {
      return throwError(new OneClickQRESetError('You cannot edit this template'));
    }

    let nextName = existingSet.name;
    if (changes.name !== undefined) {
      const nameValidation = this.validateName(changes.name, setId);
      if (!nameValidation.valid) {
        return throwError(new OneClickQRESetError(nameValidation.errorKey));
      }
      nextName = nameValidation.trimmedName;
    }

    let nextItems = existingSet.items;
    if (changes.items) {
      const itemsError = this.validateItems(changes.items, intakeFormsEnabled);
      if (itemsError) {
        return throwError(new OneClickQRESetError(itemsError));
      }
      nextItems = changes.items.map(item => ({ ...item }));
    }

    const updatedSets = sets.map(set => {
      if (set.id !== setId) {
        return set;
      }
      return {
        ...set,
        name: nextName,
        items: nextItems
      };
    });

    return this.persistSets(updatedSets);
  }

  public deleteSet(setId: string): Observable<OneClickQRESet[]> {
    const sets = this.getSets();
    const existingSet = sets.find(set => set.id === setId);
    if (!existingSet) {
      return throwError(new OneClickQRESetError('Template not found'));
    }
    if (!this.canMutateSet(existingSet)) {
      return throwError(new OneClickQRESetError('You cannot delete this template'));
    }
    return this.persistSets(sets.filter(set => set.id !== setId));
  }

  public createOrOverwriteSet(
    name: string,
    items: OneClickQRESetItem[],
    intakeFormsEnabled: boolean
  ): Observable<OneClickQRESet[]> {
    if (!this.canMutateAllSets()) {
      return throwError(new OneClickQRESetError('You cannot create a template'));
    }
    const nameValidation = this.validateName(name);
    const trimmedName = (name || '').trim();
    const matchingSet = this.getSets().find(
      set => set.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (matchingSet && this.canMutateSet(matchingSet)) {
      return this.updateSet(matchingSet.id, { name: trimmedName, items }, intakeFormsEnabled);
    }
    if (!nameValidation.valid) {
      return throwError(new OneClickQRESetError(nameValidation.errorKey));
    }
    return this.createSet(trimmedName, items, intakeFormsEnabled);
  }

  private persistSets(sets: OneClickQRESet[]): Observable<OneClickQRESet[]> {
    const clinicSetupId = this.clinicSetupService.clinicSetupId;
    if (!clinicSetupId) {
      return throwError(new OneClickQRESetError('Clinic setup is not available'));
    }

    const currentExtraConfig = this.readCurrentExtraConfig();
    const nextExtraConfig = {
      ...currentExtraConfig,
      [ONE_CLICK_QRE_SETS_EXTRA_CONFIG_KEY]: sets
    };

    return this.clinicSetupService
      .updateClinicSetup({
        id: clinicSetupId,
        extraConfig: JSON.stringify(nextExtraConfig)
      })
      .pipe(
        tap(() => {
          const extraConfigJson = JSON.stringify(nextExtraConfig);
          if (this.clinicSetupService.clinicSetup) {
            this.clinicSetupService.clinicSetup.extraConfig = extraConfigJson;
          }
          this.clinicSetupService.clinicSetupFormGroup.mergeLoadedExtraConfig({
            [ONE_CLICK_QRE_SETS_EXTRA_CONFIG_KEY]: sets
          });
        }),
        map(() => sets)
      );
  }

  private readCurrentExtraConfig(): { [key: string]: unknown } {
    const loaded = this.clinicSetupService.clinicSetupFormGroup.getLoadedExtraConfig();
    const clinicSetup = this.clinicSetupService.clinicSetup;
    if (clinicSetup && clinicSetup.extraConfig) {
      const parsed = safeJsonParse(clinicSetup.extraConfig);
      if (parsed && typeof parsed === 'object') {
        return {
          ...parsed,
          ...loaded
        };
      }
    }
    return { ...loaded };
  }

  private staffHasPrivilegedGroup(staff: Doctor): boolean {
    if (!staff.cognitoGroups) {
      return false;
    }
    return (
      staff.cognitoGroups.indexOf(CognitoGroup.Doctor) !== -1 ||
      staff.cognitoGroups.indexOf(CognitoGroup.Admin) !== -1
    );
  }

  private normalizeSets(rawSets: unknown): OneClickQRESet[] {
    if (!Array.isArray(rawSets)) {
      return [];
    }
    return rawSets
      .map(rawSet => this.normalizeSet(rawSet))
      .filter(set => !!set);
  }

  private normalizeSet(rawSet: unknown): OneClickQRESet {
    if (!rawSet || typeof rawSet !== 'object') {
      return null;
    }
    const candidate = rawSet as OneClickQRESet;
    if (!candidate.id || !candidate.name || !Array.isArray(candidate.items)) {
      return null;
    }
    const items = candidate.items
      .filter(item => item && item.id && item.kind && item.label)
      .map(item => ({
        kind: item.kind,
        id: item.id,
        label: item.label
      }));
    return {
      id: candidate.id,
      name: candidate.name,
      createdByStaffId: candidate.createdByStaffId || '',
      items
    };
  }
}
